import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Icon } from '@iconify/react';

/**
 * Staff-only modal to create a new tenant. Presentational: the caller owns
 * the mutation, so the modal only reports the trimmed name via onCreate and
 * shows an inline error when that promise rejects.
 */
export function CreateOrganizationModal({ isOpen, onClose, onCreate, isSubmitting }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || isSubmitting) return;
    setError(null);
    try {
      await onCreate(trimmed);
      onClose();
    } catch {
      setError(t('workspace.createOrganization.failed'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-80 flex items-start justify-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-organization-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white p-7 shadow-2xl"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">
              {t('workspace.createOrganization.eyebrow')}
            </p>
            <h2 id="create-organization-title" className="text-xl font-semibold text-[var(--color-text)]">
              {t('workspace.createOrganization.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workspace.createOrganization.close')}
            className="cursor-pointer flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)]"
          >
            <Icon icon="mdi:close" className="size-5" />
          </button>
        </div>

        <label htmlFor="new-organization-name" className="mb-2 block text-sm font-medium text-[var(--color-text)]">
          {t('workspace.createOrganization.nameLabel')}
        </label>
        <input
          ref={inputRef}
          id="new-organization-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('workspace.createOrganization.namePlaceholder')}
          className="w-full rounded-xl border border-[var(--color-border)] px-4 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
          }}
        />
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('workspace.createOrganization.ownerNote')}</p>
        {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="mt-7 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-lg border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
          >
            {t('workspace.createOrganization.cancel')}
          </button>
          <button
            type="submit"
            disabled={!name.trim() || isSubmitting}
            className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon icon="mdi:domain-plus" className="size-4" />
            {isSubmitting ? t('workspace.createOrganization.creating') : t('workspace.createOrganization.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

CreateOrganizationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};
